/**
 * Auth context — Gmail + Apple sign-in (Firebase Auth).
 *
 * LOCKED model: real OAuth (Google + Apple). No PIN, no OTP, no anonymous
 * (the pre-rebuild anon+PIN flow is retired in src/_legacy/).
 *
 * Sprint-1 scope: real sign-in / sign-out + session restore + a role stub.
 *
 * Round 3 (2026-05-18) — FIX 1 (#914): the real team-claim mechanism.
 * After Google/Apple sign-in (or session restore) we call the server
 * callable `resolveTeamClaimsOnSignIn`. The CF resolves the verified token
 * email against HR's `staff/` master and mints the locked custom claims
 * (isTeam/staffId/roles/...) server-side, or mints nothing for clients. We
 * then force an ID-token refresh so the new claims take effect immediately,
 * and derive the LOGIN role from those real claims (roles.ts roleFromClaims).
 * The demo `?role=` override remains ONLY as a review affordance when there
 * is no real team claim (e.g. owner walking all tracks from one client
 * sign-in) — real claims always win.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { auth, googleProvider, appleProvider, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { resolveRoleStub, roleFromClaims, type Role } from './roles';
import { devToolsEnabled } from './devFlags';

interface AuthState {
  user: User | null;
  role: Role | null;
  /**
   * Surfaces the signed-in user may SSO into (`['avbp','hr','capture']`),
   * read from the minted team claims. Empty for clients / non-team users.
   * Consumed by the AppShell launcher + the Login `?return=` handoff to
   * decide whether to attempt an SSO mint vs go straight to legacy login.
   * (Phase-0 hotfix re-implementation — see src/lib/handoff.ts.)
   */
  surfaces: string[];
  /**
   * The signed-in user's `staff/` document id, from the minted team claims
   * (`staffId` — e.g. "E016"). This is the id the agent executors resolve
   * an assignee to (`StaffResolver` → `tasks/{}.assigneeUid`), so it is the
   * id the Home "Your tasks" panel filters the live `tasks/` collection on.
   * null for clients / non-team users (no claim). Distinct from the
   * Firebase Auth `user.uid`, which is NOT the task assignee id.
   */
  staffId: string | null;
  /**
   * Phase-2 Slice 3 — the signed-in CLIENT's id (`clients/{id}` doc id, e.g.
   * "C00465"), from the `verifyClientOtp` custom-token claims
   * (`contactId` == `clientId`). This is the id the client-project resolver
   * filters `projects where clientId == clientId` on, and the id the
   * getReportDownloadUrl CF matches against. null for team / non-client users
   * (no isClient claim). A client must only ever see data keyed to THIS id.
   */
  clientId: string | null;
  /** true until the first onAuthStateChanged fires (session restore). */
  loading: boolean;
  /**
   * true while a signed-in user's TEAM CLAIMS are still being resolved
   * (the `resolveTeamClaimsOnSignIn` round-trip + token refresh). Distinct
   * from `loading`: `onAuthStateChanged` flips `loading` false the instant
   * the Firebase user is known, but the role-driving custom claims arrive a
   * tick LATER. During that gap a team user's `role` transiently reads
   * `'client'` (claimRole ?? 'client'). A role-gated guard
   * (RequireAuth roles={['admin','staff']}) that redirects on that transient
   * value bounces the user off a deep route (e.g. /messages) to Home on a
   * browser reload — the WhatsApp-inbox "refresh drops me to Home" bug. So a
   * guard MUST wait for `!loading && !claimsResolving` before trusting role.
   */
  claimsResolving: boolean;
  error: string | null;
  signInGoogle: () => Promise<void>;
  signInApple: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Role from the REAL minted custom claims (null until resolved). */
  const [claimRole, setClaimRole] = useState<Role | null>(null);
  /** true once we have a server-confirmed team claim for this user. */
  const [isTeamClaim, setIsTeamClaim] = useState(false);
  /** `surfaces[]` from the minted team claims (SSO launcher / handoff). */
  const [claimSurfaces, setClaimSurfaces] = useState<string[]>([]);
  /** `staffId` from the minted team claims — the task-assignee id. */
  const [claimStaffId, setClaimStaffId] = useState<string | null>(null);
  /** `contactId`/`clientId` from the client custom-token claims (Slice 3). */
  const [claimClientId, setClaimClientId] = useState<string | null>(null);
  /**
   * true while a signed-in user's claims are being resolved (see the
   * `claimsResolving` doc on AuthState). Seeded `true` so the very first
   * render of a session-restore (before onAuthStateChanged even fires)
   * never lets a role-gated guard act on the not-yet-known role.
   */
  const [claimsResolving, setClaimsResolving] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setLoading(false);
      if (!u) {
        setClaimRole(null);
        setIsTeamClaim(false);
        setClaimSurfaces([]);
        setClaimStaffId(null);
        setClaimClientId(null);
        // No user → nothing to resolve; the /login redirect can fire.
        setClaimsResolving(false);
        return;
      }
      // A user IS present — claims are not yet known. Hold guards from
      // acting on the transient `'client'` default until the round-trip
      // below settles (success OR catch both clear this).
      setClaimsResolving(true);
      try {
        // Server-authoritative: resolve the verified email against the
        // staff master and mint claims (or none, for clients).
        const fn = httpsCallable<
          Record<string, never>,
          { team: boolean }
        >(functions, 'resolveTeamClaimsOnSignIn');
        await fn({});
        // Force a token refresh so freshly-minted claims take effect NOW.
        const tokenResult = await u.getIdTokenResult(true);
        const claims = tokenResult.claims as Record<string, unknown>;
        setIsTeamClaim(claims.isTeam === true);
        setClaimRole(roleFromClaims(claims));
        setClaimSurfaces(
          Array.isArray(claims.surfaces)
            ? (claims.surfaces as unknown[]).map((s) => String(s))
            : [],
        );
        setClaimStaffId(
          typeof claims.staffId === 'string' && claims.staffId
            ? claims.staffId
            : null,
        );
        // Slice 3 — a CLIENT custom-token session carries isClient + contactId
        // (== clientId). Surface it ONLY for a genuine client claim; a team
        // session never has it (and must never be scoped to a clientId).
        const isClientClaim = claims.isClient === true;
        const cid =
          typeof claims.contactId === 'string' && claims.contactId
            ? claims.contactId
            : typeof claims.clientId === 'string' && claims.clientId
              ? claims.clientId
              : null;
        setClaimClientId(isClientClaim ? cid : null);
      } catch {
        // Network / billing / emulator-down: fall back to the demo stub so
        // the reviewer is never hard-blocked. Real claims win when present.
        setIsTeamClaim(false);
        setClaimRole(null);
        setClaimSurfaces([]);
        setClaimStaffId(null);
        setClaimClientId(null);
      } finally {
        // Resolution settled (either way) — guards may now trust `role`.
        setClaimsResolving(false);
      }
    });
    return unsub;
  }, []);

  // Role resolution (locked auth model: the claim drives the UI).
  //   1. A server-confirmed TEAM claim always wins → its claimRole.
  //   2. Otherwise the role comes from the RESOLVED claims — roleFromClaims
  //      returns 'client' for a signed-in user with no team claim (a real
  //      client). This is the #1190 fix: previously a client (no team claim)
  //      fell to resolveRoleStub(), which DEFAULTS to 'staff' — so a real
  //      client landed on the staff track. They must land on the client track.
  //   3. The demo `?role=` / persisted stub override is a REVIEW-ONLY
  //      affordance — honoured ONLY when devTools are enabled (?dev=1 / dev
  //      build), never in a normal production session. This lets a reviewer
  //      walk all tracks from one sign-in without mis-routing real users.
  const role: Role | null = !user
    ? null
    : isTeamClaim && claimRole
      ? claimRole
      : devToolsEnabled()
        ? resolveRoleStub()
        : (claimRole ?? 'client');

  async function run(provider: 'google' | 'apple') {
    setError(null);
    try {
      await signInWithPopup(
        auth,
        provider === 'google' ? googleProvider : appleProvider,
      );
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return; // user dismissed — not an error worth surfacing
      }
      setError(
        code === 'auth/operation-not-allowed'
          ? 'This sign-in method is not enabled yet in Firebase Auth.'
          : `Sign-in failed: ${(e as Error).message}`,
      );
    }
  }

  const value = useMemo<AuthState>(
    () => ({
      user,
      role,
      surfaces: claimSurfaces,
      staffId: claimStaffId,
      clientId: claimClientId,
      loading,
      claimsResolving,
      error,
      signInGoogle: () => run('google'),
      signInApple: () => run('apple'),
      signOut: async () => {
        await fbSignOut(auth);
      },
    }),
    [
      user,
      role,
      loading,
      claimsResolving,
      error,
      isTeamClaim,
      claimRole,
      claimSurfaces,
      claimStaffId,
      claimClientId,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within <AuthProvider>');
  return v;
}
